import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";
import { Package, ShoppingBag } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { COLORS_DARK, COLORS_LIGHT } from "./DashboardShared";

function CustomChartTooltip({ active, payload, label, devise, mode, isDark = true }) {
  if (!active || !payload || !payload.length) return null;
  const C = isDark ? COLORS_DARK : COLORS_LIGHT;
  const data = payload[0].payload;
  return (
    <div className="px-4 py-3 rounded-[10px] shadow-lg"
      style={{ backgroundColor: C.tooltipBg, border: `1px solid ${C.tooltipBorder}` }}>
      <p className="text-sm font-medium mb-1" style={{ color: isDark ? '#fff' : '#111827' }}>{data.fullDate || label}</p>
      <p className="text-lg font-bold" style={{ color: payload[0].stroke }}>
        {mode === "revenue" ? `${data.revenue?.toLocaleString('fr-FR')} ${devise}` : `${data.transactions} vente${data.transactions > 1 ? 's' : ''}`}
      </p>
      {mode === "revenue" && (
        <p className="text-xs mt-1" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>
          {data.transactions} transaction{data.transactions > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

function TopProductsTooltip({ active, payload, devise, isDark = true }) {
  if (!active || !payload || !payload.length) return null;
  const C = isDark ? COLORS_DARK : COLORS_LIGHT;
  const data = payload[0].payload;
  return (
    <div className="px-4 py-3 rounded-[10px] shadow-lg"
      style={{ backgroundColor: C.tooltipBg, border: `1px solid ${C.tooltipBorder}` }}>
      <p className="text-sm font-medium mb-1" style={{ color: isDark ? '#fff' : '#111827' }}>{data.name}</p>
      <p className="text-lg font-bold" style={{ color: isDark ? '#fff' : '#111827' }}>{data.quantity} vendu{data.quantity > 1 ? 's' : ''}</p>
      <p className="text-xs mt-1" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>CA: {data.revenue?.toLocaleString('fr-FR')} {devise}</p>
    </div>
  );
}

function CategoryTooltip({ active, payload, devise, isDark = true }) {
  if (!active || !payload || !payload.length) return null;
  const C = isDark ? COLORS_DARK : COLORS_LIGHT;
  const data = payload[0].payload;
  return (
    <div className="px-4 py-3 rounded-[10px] shadow-lg"
      style={{ backgroundColor: C.tooltipBg, border: `1px solid ${C.tooltipBorder}` }}>
      <p className="text-sm font-medium mb-1" style={{ color: isDark ? '#fff' : '#111827' }}>{data.name}</p>
      <p className="text-lg font-bold" style={{ color: data.fill }}>{data.value?.toLocaleString('fr-FR')} {devise}</p>
      <p className="text-xs mt-1" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>{data.percent?.toFixed(1)}% du CA total</p>
    </div>
  );
}

export function MainChart({ chartData, chartMode, setChartMode, primaryColor, devise, isDark, selectedPeriod, COLORS }) {
  return (
    <div className="hidden md:block">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.35 }}>
        <Card className="border-border/50">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold">Évolution des ventes</h2>
                <p className="text-sm text-muted-foreground">
                  {chartMode === "revenue" ? "Revenus" : "Transactions"} par {
                    selectedPeriod === "today" ? "heure" : selectedPeriod === "year" ? "mois" : "jour"
                  } sur la période
                </p>
              </div>
              <div className="flex items-center gap-1 p-1 bg-secondary rounded-lg">
                {["revenue", "transactions"].map(m => (
                  <button key={m} onClick={() => setChartMode(m)} data-testid={`chart-mode-${m}`}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                      chartMode === m ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {m === "revenue" ? "Revenus" : "Transactions"}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={primaryColor} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={primaryColor} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="0" stroke={COLORS.gridLine} vertical={false} />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: COLORS.axisText, fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: COLORS.axisText, fontSize: 12 }} width={50}
                    tickFormatter={(value) => chartMode === "revenue" ? (value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value) : value} />
                  <Tooltip content={<CustomChartTooltip devise={devise} mode={chartMode} isDark={isDark} />}
                    cursor={{ stroke: primaryColor, strokeOpacity: 0.2, strokeWidth: 2 }} />
                  <Area type="monotone" dataKey={chartMode === "revenue" ? "revenue" : "transactions"}
                    stroke={primaryColor} strokeWidth={2} fill="url(#chartGradient)"
                    animationBegin={0} animationDuration={1200}
                    activeDot={{ r: 6, fill: "#FFFFFF", stroke: primaryColor, strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

export function TopProductsAndCategories({ topProductsData, categoryData, salesData, primaryColor, devise, isDark, colorPalette, COLORS }) {
  const navigate = useNavigate();
  return (
    <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Top Produits */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}>
        <Card className="border-border/50 h-full">
          <CardContent className="p-5">
            <div className="mb-4">
              <h2 className="text-lg font-semibold">Top produits</h2>
              <p className="text-sm text-muted-foreground">Les plus vendus sur la période</p>
            </div>
            {topProductsData.some(p => p.quantity > 0) ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topProductsData} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="0" stroke={COLORS.gridLine} horizontal vertical={false} />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: COLORS.axisText, fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false}
                      tick={{ fill: COLORS.axisText, fontSize: 12 }} width={120}
                      tickFormatter={(value) => value.length > 15 ? value.slice(0, 15) + '...' : value} />
                    <Tooltip content={<TopProductsTooltip devise={devise} isDark={isDark} />} />
                    <Bar dataKey="quantity" radius={[0, 6, 6, 0]} cursor="pointer"
                      onClick={(data) => navigate(`/products?search=${encodeURIComponent(data.name)}`)}>
                      {topProductsData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={primaryColor} fillOpacity={1 - (index * 0.1)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${primaryColor}15` }}>
                    <Package className="w-7 h-7" style={{ color: primaryColor }} />
                  </div>
                  <p className="text-sm text-muted-foreground">Aucune vente sur cette période</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Répartition par catégorie */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.45 }}>
        <Card className="border-border/50 h-full">
          <CardContent className="p-5">
            <div className="mb-4">
              <h2 className="text-lg font-semibold">Répartition par catégorie</h2>
              <p className="text-sm text-muted-foreground">% du CA total</p>
            </div>
            {salesData?.total_sales > 0 ? (
              <>
                <div className="h-[220px] relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                        paddingAngle={2} dataKey="value" animationBegin={0} animationDuration={800}>
                        {categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip content={<CategoryTooltip devise={devise} isDark={isDark} />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{(salesData?.total_sales || 0).toLocaleString('fr-FR')}</p>
                      <p className="text-xs text-muted-foreground">{devise}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4 justify-center">
                  {categoryData.map((cat) => (
                    <div key={cat.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: cat.fill }} />
                      <span className="text-xs text-muted-foreground">{cat.name} ({cat.percent}%)</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${primaryColor}15` }}>
                    <ShoppingBag className="w-7 h-7" style={{ color: primaryColor }} />
                  </div>
                  <p className="text-sm text-muted-foreground">Aucune vente sur cette période</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
